import { eq } from "drizzle-orm";
import {
  list,
  list_group,
  list_group_item,
  type PowerSyncDrizzleDB,
  subtask,
  todo,
} from "@famidex/powersync";
import { nowISO } from "../utils/date-helpers";
import { generateUUID } from "../utils/uuid";

export async function createListMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  name: string,
  opts?: {
    color?: string;
    icon?: string;
    groupId?: string;
    themeId?: string | null;
    isBlur?: boolean;
  },
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();

  await db.transaction(async (trx) => {
    await trx.insert(list).values({
      id,
      name: name.trim(),
      color: opts?.color ?? null,
      icon: opts?.icon ?? null,
      user_id: userId,
      theme_id: opts?.themeId ?? null,
      is_blur: opts?.isBlur === false ? 0 : 1,
      created_at: now,
      updated_at: now,
    });

    if (opts?.groupId) {
      await trx.insert(list_group_item).values({
        id: generateUUID(),
        list_id: id,
        group_id: opts.groupId,
        user_id: userId,
        added_at: now,
      });
    }
  });

  return id;
}

export async function updateListMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{
    name: string;
    color: string | null;
    icon: string | null;
    theme_id: string | null;
    is_blur: number;
  }>,
): Promise<void> {
  const finalUpdates = { ...updates };
  if (finalUpdates.name !== undefined) {
    finalUpdates.name = finalUpdates.name.trim();
  }

  await db
    .update(list)
    .set({ ...finalUpdates, updated_at: nowISO() })
    .where(eq(list.id, id));
}

export async function createListGroupMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  name: string,
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(list_group).values({
    id,
    name: name.trim(),
    user_id: userId,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateListGroupMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{ name: string }>,
): Promise<void> {
  const finalUpdates = { ...updates };
  if (finalUpdates.name !== undefined) {
    finalUpdates.name = finalUpdates.name.trim();
  }

  await db
    .update(list_group)
    .set({ ...finalUpdates, updated_at: nowISO() })
    .where(eq(list_group.id, id));
}

export async function assignListToGroupMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  listId: string,
  groupId: string | null,
): Promise<void> {
  await db.transaction(async (trx) => {
    if (groupId) {
      await trx.delete(list_group_item).where(eq(list_group_item.list_id, listId));
      await trx.insert(list_group_item).values({
        id: generateUUID(),
        list_id: listId,
        group_id: groupId,
        user_id: userId,
        added_at: nowISO(),
      });
    } else {
      await trx.delete(list_group_item).where(eq(list_group_item.list_id, listId));
    }
  });
}

/**
 * Soft-delete de una lista (papelera, ACTIVITY-CORE L2): solo marca
 * `deleted_at`. Sus tareas/grupos quedan intactos (ocultos mientras la lista
 * esté en la papelera) y reaparecen al restaurar. La purga definitiva es
 * `purgeListMutation`.
 */
export async function deleteListOptimisticMutation(
  db: PowerSyncDrizzleDB,
  id: string,
): Promise<void> {
  const now = nowISO();
  await db.update(list).set({ deleted_at: now, updated_at: now }).where(eq(list.id, id));
}

/** Restaura una lista de la papelera (sus tareas vuelven con ella). */
export async function restoreListMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  await db.update(list).set({ deleted_at: null, updated_at: nowISO() }).where(eq(list.id, id));
}

/**
 * Purga DEFINITIVA de una lista desde la papelera: borra sus subtareas, sus
 * tareas, sus asociaciones a grupos y la propia lista. Irreversible en el
 * cliente; el servidor conserva los snapshots en `activity`.
 */
export async function purgeListMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  await db.transaction(async (trx) => {
    await trx.delete(subtask).where(eq(subtask.list_id, id));
    await trx.delete(todo).where(eq(todo.list_id, id));
    await trx.delete(list_group_item).where(eq(list_group_item.list_id, id));
    await trx.delete(list).where(eq(list.id, id));
  });
}

export async function deleteListGroupOptimisticMutation(
  db: PowerSyncDrizzleDB,
  id: string,
): Promise<void> {
  await db.transaction(async (trx) => {
    await trx.delete(list_group_item).where(eq(list_group_item.group_id, id));
    await trx.delete(list_group).where(eq(list_group.id, id));
  });
}
